import { useState, useMemo } from 'react';
import { KpiStrip } from '@/components/common/KpiStrip';
import { ExportMenu } from '@/components/common/ExportMenu';
import { HardwareDistribution } from '@/components/dashboard/HardwareDistribution';
import { ProfileBreakdown } from '@/components/dashboard/ProfileBreakdown';
import { PortfolioTreemap } from '@/components/dashboard/PortfolioTreemap';
import { VersionCompliance } from '@/components/dashboard/VersionCompliance';
import { VersionDistribution } from '@/components/dashboard/VersionDistribution';
import { UptimeStats } from '@/components/dashboard/UptimeStats';
import { NodeListDrawer } from '@/components/dashboard/NodeListDrawer';
import { PageLoader } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { X, Server } from '@/utils/icons';
import { useFleetSummary, usePortfolioMetrics, useResourceSummary } from '@/hooks/useMetrics';
import { useNodes } from '@/hooks/useNodes';
import { parseVendor, parseHardware } from '@/utils/calculations';
import { useDashboardStore } from '@/stores/useDashboardStore';
import {
  createBrandedPdf,
  addMetricRow,
  addSectionHeading,
  addBrandedTable,
  downloadPdf,
  loadLogoForPdf,
  exportFilename,
} from '@/utils/pdf';
import { downloadCsv, formatExportDate } from '@/utils/csv';

export function FleetInventory() {
  const { summary, loading: summaryLoading } = useFleetSummary();
  const { nodes, loading: nodesLoading } = useNodes({ limit: 1000 });
  const { portfolios, loading: portfoliosLoading } = usePortfolioMetrics();
  const { summary: resourceSummary } = useResourceSummary();

  // Filter state — shared global store so filters persist across page navigation
  const {
    selectedProfile,
    setSelectedProfile,
    selectedVendor,
    setSelectedVendor,
    selectedVersion,
    setSelectedVersion,
    selectedPortfolio,
    setSelectedPortfolio,
  } = useDashboardStore();

  // Node List Drawer state
  const [nodeListFilter, setNodeListFilter] = useState<{
    softwareVersion?: string;
    vendor?: string;
    profile?: string;
    portfolio?: string;
  } | null>(null);
  const [nodeListTitle, setNodeListTitle] = useState('');

  // Open the node list drawer showing nodes matching all active filters.
  // Accepts overrides for values just set via setState (not yet flushed).
  const openFilteredDrawer = (overrides?: {
    profile?: string;
    vendor?: string;
    version?: string;
    portfolio?: string;
  }) => {
    const profile = overrides?.profile ?? selectedProfile;
    const vendor = overrides?.vendor ?? selectedVendor;
    const version = overrides?.version ?? selectedVersion;
    const portfolio = overrides?.portfolio ?? selectedPortfolio;

    const parts: string[] = [];
    if (profile) parts.push(`Profile: ${profile}`);
    if (vendor) parts.push(`Hardware: ${vendor}`);
    if (version) parts.push(`Version: ${version}`);
    if (portfolio) parts.push(`Portfolio: ${portfolio}`);
    setNodeListTitle(parts.join(' · ') || 'Nodes');

    setNodeListFilter({
      ...(profile && { profile }),
      ...(vendor && { vendor }),
      ...(version && { softwareVersion: version }),
      ...(portfolio && { portfolio }),
    });
  };

  // ── Cross-filtering: filter nodes and recompute summary stats ──
  const hasFilter = !!(selectedProfile || selectedPortfolio || selectedVersion || selectedVendor);

  const filteredNodes = useMemo(() => {
    if (!hasFilter) return nodes;
    let result = nodes;
    if (selectedProfile) {
      result = result.filter((n) => n.profileName === selectedProfile);
    }
    if (selectedPortfolio) {
      result = result.filter((n) => (n.portfolio || 'Unassigned') === selectedPortfolio);
    }
    if (selectedVersion) {
      result = result.filter((n) => n.softwareVersion === selectedVersion);
    }
    if (selectedVendor) {
      result = result.filter((n) => parseVendor(n) === selectedVendor);
    }
    return result;
  }, [nodes, hasFilter, selectedProfile, selectedPortfolio, selectedVersion, selectedVendor]);

  const filteredSummary = useMemo(() => {
    if (!summary) return null;
    if (!hasFilter) return summary;

    const totalNodes = filteredNodes.length;
    const aliveNodes = filteredNodes.filter((n) => n.currentNodeState === 'ALIVE').length;
    const unreachableNodes = totalNodes - aliveNodes;

    const profileBreakdown: Record<string, number> = {};
    filteredNodes.forEach((n) => {
      const profile = n.profileName || 'Unknown';
      profileBreakdown[profile] = (profileBreakdown[profile] || 0) + 1;
    });

    const versionDistribution: Record<string, number> = {};
    filteredNodes.forEach((n) => {
      const version = n.softwareVersion || 'Unknown';
      versionDistribution[version] = (versionDistribution[version] || 0) + 1;
    });

    const latestVersion = summary.versionCompliance.latestVersion;
    const compliant = latestVersion
      ? filteredNodes.filter((n) => n.softwareVersion === latestVersion).length
      : 0;
    const outdated = totalNodes - compliant;

    // Recompute hardware vendors from filtered nodes
    const hardwareVendors: Record<string, number> = {};
    filteredNodes.forEach((n) => {
      const vendor = parseVendor(n);
      hardwareVendors[vendor] = (hardwareVendors[vendor] || 0) + 1;
    });

    // Recompute hardware uptime stats from filtered nodes with boot time
    // Matches backend logic: only Edge + Virtual Edge profiles with bootTime and lastContactedAt
    const now = Date.now();
    const edgeProfiles = new Set(['Edge', 'Virtual Edge']);
    const edgeNodes = filteredNodes.filter((n) => edgeProfiles.has(n.profileName ?? ''));
    const nodesWithBootTime = edgeNodes
      .filter((n) => n.bootTime && n.lastContactedAt)
      .map((n) => ({
        name: n.name,
        uptimeDays: (now - new Date(n.bootTime!).getTime()) / (1000 * 60 * 60 * 24),
      }))
      .filter((n) => n.uptimeDays >= 0);

    let uptimeStats = summary.uptimeStats;
    if (nodesWithBootTime.length > 0) {
      const avg =
        Math.round(
          (nodesWithBootTime.reduce((sum, n) => sum + n.uptimeDays, 0) / nodesWithBootTime.length) *
            10
        ) / 10;
      const longest = nodesWithBootTime.reduce((best, n) =>
        n.uptimeDays > best.uptimeDays ? n : best
      );
      const shortest = nodesWithBootTime.reduce((best, n) =>
        n.uptimeDays < best.uptimeDays ? n : best
      );
      uptimeStats = {
        ...summary.uptimeStats,
        avgUptimeDays: avg,
        longestUptimeDays: Math.round(longest.uptimeDays * 10) / 10,
        longestUptimeNodeName: longest.name,
        shortestUptimeDays: Math.round(shortest.uptimeDays * 10) / 10,
        shortestUptimeNodeName: shortest.name,
        shortestUptimeInsight: null,
      };
    } else if (filteredNodes.length === 0) {
      uptimeStats = {
        ...summary.uptimeStats,
        avgUptimeDays: 0,
        longestUptimeDays: 0,
        longestUptimeNodeName: null,
        shortestUptimeDays: 0,
        shortestUptimeNodeName: null,
        shortestUptimeInsight: null,
      };
    }

    // Recompute network uptime stats from filtered ALIVE edge nodes with nodeStateChangedAt
    // Matches backend logic: ALIVE state + nodeStateChangedAt + lastContactedAt present
    const aliveEdgeNodes = edgeNodes
      .filter((n) => n.currentNodeState === 'ALIVE' && n.nodeStateChangedAt && n.lastContactedAt)
      .map((n) => ({
        name: n.name,
        uptimeDays: (now - new Date(n.nodeStateChangedAt!).getTime()) / (1000 * 60 * 60 * 24),
      }))
      .filter((n) => n.uptimeDays >= 0);

    let networkUptimeStats = summary.networkUptimeStats;
    if (aliveEdgeNodes.length > 0) {
      const avg =
        Math.round(
          (aliveEdgeNodes.reduce((sum, n) => sum + n.uptimeDays, 0) / aliveEdgeNodes.length) * 10
        ) / 10;
      const longest = aliveEdgeNodes.reduce((best, n) =>
        n.uptimeDays > best.uptimeDays ? n : best
      );
      const shortest = aliveEdgeNodes.reduce((best, n) =>
        n.uptimeDays < best.uptimeDays ? n : best
      );
      networkUptimeStats = {
        avgUptimeDays: avg,
        longestUptimeDays: Math.round(longest.uptimeDays * 10) / 10,
        longestUptimeNodeName: longest.name,
        shortestUptimeDays: Math.round(shortest.uptimeDays * 10) / 10,
        shortestUptimeNodeName: shortest.name,
      };
    } else if (filteredNodes.length === 0) {
      networkUptimeStats = {
        avgUptimeDays: 0,
        longestUptimeDays: 0,
        longestUptimeNodeName: null,
        shortestUptimeDays: 0,
        shortestUptimeNodeName: null,
      };
    }

    return {
      ...summary,
      totalNodes,
      aliveNodes,
      unreachableNodes,
      profileBreakdown,
      versionDistribution,
      versionCompliance: { compliant, outdated, latestVersion },
      hardwareVendors,
      uptimeStats,
      networkUptimeStats,
    };
  }, [summary, filteredNodes, hasFilter]);

  const filteredPortfolios = useMemo(() => {
    if (!hasFilter || !(selectedProfile || selectedVersion || selectedVendor)) return portfolios;

    const portfolioMap: Record<string, { total: number; alive: number }> = {};
    filteredNodes.forEach((n) => {
      const p = n.portfolio || 'Unassigned';
      if (!portfolioMap[p]) portfolioMap[p] = { total: 0, alive: 0 };
      portfolioMap[p].total++;
      if (n.currentNodeState === 'ALIVE') portfolioMap[p].alive++;
    });

    return Object.entries(portfolioMap).map(([portfolio, counts]) => ({
      portfolio,
      totalNodes: counts.total,
      aliveNodes: counts.alive,
      unreachableNodes: counts.total - counts.alive,
      uptimePercent: counts.total > 0 ? (counts.alive / counts.total) * 100 : 0,
      longestOfflineDays: 0,
      longestOfflineNodeName: null,
    }));
  }, [portfolios, filteredNodes, hasFilter, selectedProfile, selectedVersion, selectedVendor]);

  const handleExportPdf = async () => {
    const s = filteredSummary;
    if (!s || !filteredNodes.length) return;

    const logoData = await loadLogoForPdf();
    const portfolioSet = new Set(filteredNodes.map((n) => n.portfolio || 'Unassigned'));

    const layout = createBrandedPdf({
      title: 'Inventory Report',
      subtitle: `${s.totalNodes} total Nodes across ${portfolioSet.size} portfolios`,
      logoData,
    });

    // Summary metrics
    let y = addMetricRow(
      layout.doc,
      [
        { label: 'Total Nodes', value: String(s.totalNodes) },
        { label: 'Edge Nodes', value: String(s.profileBreakdown['Edge'] || 0) },
        { label: 'Virtual Edges', value: String(s.profileBreakdown['Virtual Edge'] || 0) },
        { label: 'Clusters', value: String(resourceSummary?.clusters ?? 0) },
        {
          label: 'Cloud Connectors',
          value: String(s.profileBreakdown['Cloud Connector'] || 0),
        },
      ],
      layout.startY,
      layout
    );

    // Node inventory table
    y = addSectionHeading(layout.doc, 'Node Inventory', y, layout);
    y = addBrandedTable(
      layout.doc,
      ['Name', 'Portfolio', 'Profile', 'Version', 'State', 'Services'],
      filteredNodes.map((n) => [
        n.name,
        n.portfolio || 'Unassigned',
        n.profileName,
        n.softwareVersion,
        n.currentNodeState,
        n.serviceCount ?? 0,
      ]),
      y,
      layout
    );

    // Version distribution table
    y = addSectionHeading(layout.doc, 'Version Distribution', y + 6, layout);
    addBrandedTable(
      layout.doc,
      ['Version', 'Node Count'],
      Object.entries(s.versionDistribution)
        .sort((a, b) => b[1] - a[1])
        .map(([version, count]) => [version, count]),
      y,
      layout
    );

    downloadPdf(layout.doc, exportFilename('fleet-inventory', 'pdf'));
  };

  const handleExportCsv = () => {
    if (!filteredNodes.length) return;

    downloadCsv(
      filteredNodes.map((n) => ({
        Name: n.name,
        Portfolio: n.portfolio || 'Unassigned',
        Profile: n.profileName || '',
        'Software Version': n.softwareVersion || '',
        State: n.currentNodeState,
        Services: n.serviceCount ?? 0,
        'Hardware S/N': n.hardwareSerialNumber || '',
        Vendor: parseHardware(n).vendor,
        Model: parseHardware(n).model || '',
        'Last Contact': formatExportDate(n.lastContactedAt),
        City: n.city || '',
        'State/Region': n.state || '',
        Country: n.country || '',
      })),
      exportFilename('fleet-inventory', 'csv'),
      { report: 'Inventory' }
    );
  };

  if (summaryLoading || nodesLoading) {
    return <PageLoader />;
  }

  return (
    <div className="dls-space-y-6">
      <div className="dls-flex dls-items-center dls-justify-between dls-flex-wrap dls-gap-4">
        <div>
          <h1 className="dls-page-title dls-flex dls-items-center dls-gap-2"><Server className="dls-h-7 dls-w-7 dls-text-neeve-primary" />Inventory</h1>
          <p className="dls-text-muted-foreground">Hardware, software, and portfolio distribution</p>
        </div>
        <div className="dls-flex dls-items-center dls-gap-2 dls-flex-wrap dls-justify-end">
          {selectedProfile && (
            <div className="dls-flex dls-items-center dls-gap-1.5 dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-medium">
              <button className="hover:dls-underline" onClick={() => openFilteredDrawer()}>
                Profile: {selectedProfile}
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="dls-h-3.5 dls-w-3.5 hover:dls-bg-transparent"
                onClick={() => setSelectedProfile(null)}
              >
                <X className="dls-h-2.5 dls-w-2.5" />
              </Button>
            </div>
          )}
          {selectedVendor && (
            <div className="dls-flex dls-items-center dls-gap-1.5 dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-medium">
              <button className="hover:dls-underline" onClick={() => openFilteredDrawer()}>
                Hardware: {selectedVendor}
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="dls-h-3.5 dls-w-3.5 hover:dls-bg-transparent"
                onClick={() => {
                  setSelectedVendor(null);
                  setNodeListFilter(null);
                }}
              >
                <X className="dls-h-2.5 dls-w-2.5" />
              </Button>
            </div>
          )}
          {selectedVersion && (
            <div className="dls-flex dls-items-center dls-gap-1.5 dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-medium">
              <button className="hover:dls-underline" onClick={() => openFilteredDrawer()}>
                Version: {selectedVersion}
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="dls-h-3.5 dls-w-3.5 hover:dls-bg-transparent"
                onClick={() => {
                  setSelectedVersion(null);
                  setNodeListFilter(null);
                }}
              >
                <X className="dls-h-2.5 dls-w-2.5" />
              </Button>
            </div>
          )}
          {selectedPortfolio && (
            <div className="dls-flex dls-items-center dls-gap-1.5 dls-rounded-full dls-bg-secondary dls-text-secondary-foreground dls-px-2.5 dls-py-0.5 dls-text-xs dls-font-medium">
              <button className="hover:dls-underline" onClick={() => openFilteredDrawer()}>
                Portfolio: {selectedPortfolio}
              </button>
              <Button
                variant="ghost"
                size="icon"
                className="dls-h-3.5 dls-w-3.5 hover:dls-bg-transparent"
                onClick={() => setSelectedPortfolio(null)}
              >
                <X className="dls-h-2.5 dls-w-2.5" />
              </Button>
            </div>
          )}
          {hasFilter && (
            <Button
              variant="ghost"
              size="sm"
              className="dls-h-6 dls-text-xs"
              onClick={() => {
                setSelectedProfile(null);
                setSelectedVendor(null);
                setSelectedVersion(null);
                setSelectedPortfolio(null);
                setNodeListFilter(null);
              }}
            >
              Clear all
            </Button>
          )}
          <ExportMenu
            onExportPdf={handleExportPdf}
            onExportCsv={handleExportCsv}
            label="Export Inventory"
          />
        </div>
      </div>

      {/* Summary stats */}
      {filteredSummary && (
        <KpiStrip
          items={[
            { label: 'Total Nodes', value: filteredSummary.totalNodes, tooltip: 'Total monitored Nodes across all profiles and portfolios.' },
            { label: 'Edge Nodes', value: filteredSummary.profileBreakdown['Edge'] || 0, tooltip: 'Physical edge hardware deployed at branch locations.' },
            { label: 'Virtual Edges', value: filteredSummary.profileBreakdown['Virtual Edge'] || 0, tooltip: 'Edge instances deployed as virtual machines on HyperV, VMware, or similar hypervisors.' },
            { label: 'Clusters', value: resourceSummary?.clusters ?? 0, tooltip: 'HA clusters providing redundancy and failover across node pairs.' },
            { label: 'Cloud Connectors', value: filteredSummary.profileBreakdown['Cloud Connector'] || 0, tooltip: 'Virtual instances running in cloud environments (AWS, Azure, etc.).' },
          ]}
        />
      )}

      {/* Empty state when filters produce no results */}
      {hasFilter && filteredNodes.length === 0 && (
        <div className="dls-flex dls-flex-col dls-items-center dls-justify-center dls-py-12 dls-text-center">
          <p className="dls-text-lg dls-font-medium dls-mb-1">No nodes match the current filters</p>
          <p className="dls-text-sm dls-text-muted-foreground dls-mb-4">Try removing some filters to see more results</p>
          <Button
            variant="secondary"
            onClick={() => {
              setSelectedProfile(null);
              setSelectedVendor(null);
              setSelectedVersion(null);
              setSelectedPortfolio(null);
              setNodeListFilter(null);
            }}
          >
            Clear all filters
          </Button>
        </div>
      )}

      {/* Profile distribution + Geographic heatmap side by side */}
      <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
        {filteredSummary && (
          <ProfileBreakdown
            breakdown={filteredSummary.profileBreakdown}
            selectedProfile={selectedProfile}
            onSelect={setSelectedProfile}
          />
        )}
        {!portfoliosLoading && filteredPortfolios.length > 0 && (
          <PortfolioTreemap
            portfolios={filteredPortfolios}
            selectedPortfolio={selectedPortfolio}
            onSelect={setSelectedPortfolio}
          />
        )}
      </div>

      {/* Version distribution + compliance side by side */}
      <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
        {filteredSummary && (
          <VersionDistribution
            versionDistribution={filteredSummary.versionDistribution}
            latestVersion={filteredSummary.versionCompliance.latestVersion}
            selectedVersion={selectedVersion}
            onSelect={(version) => {
              setSelectedVersion(version);
              if (version) openFilteredDrawer({ version });
            }}
          />
        )}
        {filteredSummary && (
          <VersionCompliance
            compliant={filteredSummary.versionCompliance.compliant}
            outdated={filteredSummary.versionCompliance.outdated}
            latestVersion={filteredSummary.versionCompliance.latestVersion}
            totalNodes={filteredSummary.totalNodes}
            onSelect={(type, value) => {
              if (type === 'outdated') {
                setNodeListTitle('Nodes Needing Upgrade');
                setNodeListFilter({ softwareVersion: 'outdated' });
              } else if (type === 'version') {
                setNodeListTitle(`Nodes on version ${value}`);
                setNodeListFilter({ softwareVersion: value });
              }
            }}
          />
        )}
      </div>

      <div className="dls-grid dls-gap-6 lg:dls-grid-cols-2">
        {/* Hardware vendors */}
        {filteredSummary && (
          <HardwareDistribution
            hardwareVendors={filteredSummary.hardwareVendors}
            selectedVendor={selectedVendor}
            onSelect={(vendor) => {
              setSelectedVendor(vendor);
              if (vendor) openFilteredDrawer({ vendor });
            }}
          />
        )}

        {/* Uptime stats */}
        {filteredSummary && (
          <UptimeStats
            networkUptimeStats={filteredSummary.networkUptimeStats}
            uptimeStats={filteredSummary.uptimeStats}
          />
        )}
      </div>

      {/* Node List Drawer */}
      <NodeListDrawer
        isOpen={!!nodeListFilter}
        onClose={() => setNodeListFilter(null)}
        title={nodeListTitle}
        filter={nodeListFilter}
      />
    </div>
  );
}
